import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import ProgressBar from './ProgressBar';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const FundraiserDetailPage = ({ fundraisers, onDonationComplete }) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [donationAmount, setDonationAmount] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDonationChoice, setShowDonationChoice] = useState(false);

  const fundraiser = fundraisers.find(f => f.id === parseInt(id));

  useEffect(() => {
    const success = searchParams.get('success');
    const amount = searchParams.get('amount');
    const canceled = searchParams.get('canceled');

    if (success === 'true' && amount && fundraiser) {
      const donationAmount = parseFloat(amount);
      onDonationComplete(fundraiser.id, donationAmount);
      setSuccessMessage(`Thank you! Your donation of $${donationAmount} has been processed successfully. Amounts will update within 30 seconds.`);

      setSearchParams({});
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again if you wish.');
      setSearchParams({});
    }
  }, [searchParams, fundraiser, onDonationComplete, setSearchParams]);

  // Reset donation choice dialog when fundraiser changes
  useEffect(() => {
    setShowDonationChoice(false);
    setError('');
  }, [id]);

  if (!fundraiser) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Fundraiser not found</h2>
        <Link to="/" className="text-blue-600 hover:text-blue-800">
          ← Back to all fundraisers
        </Link>
      </div>
    );
  }

  const handleDonate = async (e) => {
    e.preventDefault();

    if (!donationAmount || isNaN(donationAmount) || parseFloat(donationAmount) <= 0) {
      setError('Please enter a valid donation amount');
      return;
    }

    // Check if fundraiser has valid GoFundMe URL (not null, not empty)
    const hasGoFundMe = fundraiser.gofundme_url && fundraiser.gofundme_url.trim() !== '';

    // If fundraiser has GoFundMe URL, show choice dialog
    if (hasGoFundMe && !showDonationChoice) {
      setShowDonationChoice(true);
      return;
    }

    // If no GoFundMe URL, make sure choice dialog is not shown
    if (!hasGoFundMe) {
      setShowDonationChoice(false);
    }

    setIsLoading(true);
    setError('');

    try {
      const stripe = await stripePromise;

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(donationAmount),
          fundraiserName: fundraiser.name,
          fundraiserId: fundraiser.id,
          isAnonymous: isAnonymous
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Check if it's a demo mode error
        if (errorData.demoMode) {
          setError('💳 ' + errorData.error);
          setIsLoading(false);
          return;
        }

        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();

      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Error processing donation. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDonateHere = async () => {
    setShowDonationChoice(false);
    setIsLoading(true);
    setError('');

    try {
      const stripe = await stripePromise;

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(donationAmount),
          fundraiserName: fundraiser.name,
          fundraiserId: fundraiser.id,
          isAnonymous: isAnonymous
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.demoMode) {
          setError('💳 ' + errorData.error);
          setIsLoading(false);
          return;
        }

        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();

      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Error processing donation. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDonateGoFundMe = () => {
    if (fundraiser.gofundme_url) {
      window.open(fundraiser.gofundme_url, '_blank');
    }
    setShowDonationChoice(false);
  };

  const suggestedAmounts = [25, 50, 100, 250];
  const showCustomAmount = donationAmount && parseFloat(donationAmount) > 250;

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/" className="text-blue-600 hover:text-blue-800 mb-4 sm:mb-6 inline-block">
        ← Back to all fundraisers
      </Link>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3">
              <img
                src={fundraiser.photo}
                alt={fundraiser.name}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>

            <div className="md:w-2/3 p-4 sm:p-6 md:p-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">{fundraiser.name}</h1>

              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Fundraising Progress</h3>
                <ProgressBar
                  goal={fundraiser.goal}
                  raised={fundraiser.raised}
                  showLegend={true}
                />
              </div>

              {fundraiser.topDonor && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🏆</span>
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-gray-700">Top Donor</p>
                      <p className="text-sm sm:text-base font-bold text-yellow-700">{fundraiser.topDonor.name}</p>
                      <p className="text-xs sm:text-sm text-gray-600">${fundraiser.topDonor.amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
          <div className="prose text-gray-600">
            <p className="text-sm sm:text-base">{fundraiser.fullBio}</p>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Support {fundraiser.name.split(' ')[0]}'s Mission</h2>

          <form onSubmit={handleDonate} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Donation Amount
              </label>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-3 sm:mb-4">
                {suggestedAmounts.map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setDonationAmount(amount.toString())}
                    className={`py-2 px-3 sm:px-4 border rounded-lg text-center transition-colors text-sm sm:text-base ${
                      donationAmount === amount.toString()
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const input = document.querySelector('input[type="number"]');
                    if (input) {
                      input.focus();
                      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="py-2 px-3 sm:px-4 border rounded-lg text-center transition-colors text-sm sm:text-base border-gray-300 hover:border-blue-300"
                >
                  $250+
                </button>
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-8 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Donate anonymously (your name won't appear as top donor)
                </span>
              </label>
            </div>

            {error && (
              <div className="text-red-600 text-xs sm:text-sm bg-red-50 p-2 sm:p-3 rounded-lg">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="text-green-600 text-xs sm:text-sm bg-green-50 p-2 sm:p-3 rounded-lg">
                {successMessage}
              </div>
            )}

            {showDonationChoice && fundraiser.gofundme_url && fundraiser.gofundme_url.trim() !== '' ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Choose Where to Donate</h3>
                  <p className="text-sm text-blue-700 mb-4">
                    You can donate via GoFundMe or directly on this site. Please note the fee differences:
                  </p>
                </div>

                <div className="grid gap-3">
                  <button
                    type="button"
                    onClick={handleDonateHere}
                    disabled={isLoading}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors text-sm sm:text-base"
                  >
                    Donate Here (Lower Fees - Recommended)
                  </button>

                  <button
                    type="button"
                    onClick={handleDonateGoFundMe}
                    className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm sm:text-base"
                  >
                    Donate via GoFundMe (Higher Fees)
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowDonationChoice(false)}
                    className="w-full bg-white text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-50 border border-gray-300 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isLoading || !donationAmount}
                className="w-full bg-blue-600 text-white py-3 px-4 sm:px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
              >
                {isLoading ? 'Processing...' : `Donate $${donationAmount || '0'}`}
              </button>
            )}
          </form>

          <div className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-500">
            <p>💳 Secure payment processed by Stripe</p>
            <p>🔒 Your payment information is encrypted and secure</p>
            <p>📧 You'll receive an email receipt after your donation</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraiserDetailPage;